import { getCurrentApi } from './baseAPI';
import { getEnvironmentConfig } from '../config/apiConfig';
import ErrorHandler from '../utils/errorHandler';
import axios from 'axios';

// Direct request function for admin API endpoints (only for user management)
const directAdminRequest = async (url, options = {}) => {
  const token = localStorage.getItem('admin-token');
  if (!token) {
    throw new Error('No admin token found. Please authenticate first.');
  }

  try {
    const fullUrl = `${getEnvironmentConfig().authServiceEndpoint}${url}`;
    console.log('Direct admin request:', {
      url,
      fullUrl,
      method: options.method || 'GET',
      token: token ? `${token.substring(0, 20)}...` : 'missing'
    });

    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getEnvironmentConfig().apiConfig.ADMIN_API_KEY,
        ...options.headers
      },
      timeout: 10000
    });

    return response;
  } catch (error) {
    console.error('Direct admin request failed:', {
      url,
      fullUrl: `${getEnvironmentConfig().authServiceEndpoint}${url}`,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    throw error;
  }
};

class GroupsAPI {
  // Create group
  async createGroup(groupData) {
    try {
      const api = await getCurrentApi();
      
      // Transform the data to avoid globalMaxLeverage issues
      const transformedData = this.transformGroupData(groupData);
      
      console.log('Creating group with transformed data:', transformedData);
      const response = await api.post('/groups', transformedData);
      return response.data;
    } catch (error) {
      console.error('Failed to create group:', error);
      throw this.handleError(error);
    }
  }

  // Update group
  async updateGroup(groupId, updateData) {
    try {
      const api = await getCurrentApi();
      
      // Transform the data to avoid globalMaxLeverage issues
      const transformedData = this.transformGroupData(updateData);
      
      console.log(`Updating group ${groupId} with transformed data:`, transformedData);
      console.log('Full request details:', {
        method: 'PUT',
        url: `/groups/${groupId}`,
        data: transformedData,
        dataTypeKeys: Object.keys(transformedData)
      });
      
      // Based on backend requirements, wrap the data in updateData object
      const requestData = { updateData: transformedData };
      console.log('Sending request with updateData structure:', requestData);
      
      const response = await api.put(`/groups/${groupId}`, requestData);
      console.log('Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update group ${groupId}:`, error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      throw this.handleError(error);
    }
  }

  // Get default group
  async getDefaultGroup() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/groups/default/group');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch default group:', error);
      throw this.handleError(error);
    }
  }

  // Delete group
  async deleteGroup(groupId) {
    try {
      console.log('API: Deleting group with ID:', groupId);
      
      // Validate required parameters
      const validation = ErrorHandler.validateParams({ groupId }, ['groupId']);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const api = await getCurrentApi();
      const response = await api.delete(`/groups/${groupId}`);
      
      console.log('API: Delete group response:', response.data);
      
      // Parse response to handle inconsistent structures
      const parsedResponse = ErrorHandler.parseResponse(response.data);
      
      if (!parsedResponse.success) {
        throw new Error(parsedResponse.error || 'Failed to delete group');
      }
      
      return parsedResponse;
    } catch (error) {
      console.error(`Failed to delete group ${groupId}:`, error);
      const handledError = ErrorHandler.handleError(error);
      throw new Error(handledError.error);
    }
  }

  // Get all groups
  async getAllGroups() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/groups');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch all groups:', error);
      throw this.handleError(error);
    }
  }

  // Get group details
  async getGroupDetails(groupId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch group details for ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Add user to group
  async addUserToGroup(groupId, userId) {
    try {
      console.log(`Calling POST /groups/${groupId}/users with userId:`, userId);
      const api = await getCurrentApi();
      
      // Send userId directly to API - the backend handles both emails and user IDs
      // Wallet-auth users have 'id' field that can be used directly
      // Email-based users have 'email' field
      const response = await api.post(`/groups/${groupId}/users`, { userId });
      console.log(`POST /groups/${groupId}/users with body:`, { userId });
      console.log(`POST /groups/${groupId}/users response:`, response);
      return response.data;
    } catch (error) {
      console.error(`Failed to add user ${userId} to group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Remove user from group
  async removeUserFromGroup(groupId, userId) {
    try {
      console.log(`Calling DELETE /groups/${groupId}/users/${userId}`);
      const api = await getCurrentApi();
      
      // Send userId directly to API - the backend handles both emails and user IDs
      // Wallet-auth users have 'id' field that can be used directly
      // Email-based users have 'email' field
      const response = await api.delete(`/groups/${groupId}/users/${userId}`);
      console.log(`DELETE /groups/${groupId}/users/${userId} response:`, response);
      return response.data;
    } catch (error) {
      console.error(`Failed to remove user ${userId} from group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Get users in group
  async getUsersInGroup(groupId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/groups/${groupId}/users`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch users in group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Add multiple users to group
  async addUsersToGroup(groupId, userIds) {
    try {
      const api = await getCurrentApi();
      const response = await api.post(`/groups/${groupId}/users/bulk`, { userIds });
      return response.data;
    } catch (error) {
      console.error(`Failed to add users to group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Remove multiple users from groups
  async removeUsersFromGroups(userIds) {
    try {
      const api = await getCurrentApi();
      const response = await api.delete('/groups/users/bulk', { data: { userIds } });
      return response.data;
    } catch (error) {
      console.error('Failed to remove users from groups:', error);
      throw this.handleError(error);
    }
  }

  // Add instrument to group
  // instrument can be a string (symbol) or object { symbol, spread_pipettes }
  async addInstrumentToGroup(groupId, instrument, spreadPipettes = '') {
    try {
      const api = await getCurrentApi();
      const payload = typeof instrument === 'object' 
        ? { instrument: instrument.symbol, spread_pipettes: instrument.spread_pipettes || spreadPipettes }
        : { instrument, spread_pipettes: spreadPipettes };
      const response = await api.post(`/groups/${groupId}/instruments`, payload);
      return response.data;
    } catch (error) {
      console.error(`Failed to add instrument ${instrument} to group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Remove instrument from group
  async removeInstrumentFromGroup(groupId, instrument) {
    try {
      const api = await getCurrentApi();
      const response = await api.delete(`/groups/${groupId}/instruments/${instrument}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to remove instrument ${instrument} from group ${groupId}:`, error);
      throw this.handleError(error);
    }
  }

  // Get user group parameters
  async getUserGroupParameters(userId, category = null) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      
      const queryString = params.toString();
      const url = queryString ?
        `/groups/users/${userId}/group-parameters?${queryString}` :
        `/groups/users/${userId}/group-parameters`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch group parameters for user ${userId}:`, error);
      throw this.handleError(error);
    }
  }

  // Get instrument leverage for user
  async getInstrumentLeverage(userId, instrument) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/groups/users/${userId}/instrument-leverage/${instrument}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch instrument leverage for user ${userId}:`, error);
      throw this.handleError(error);
    }
  }

  // Handle API errors consistently
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const errorData = error.response.data;
      const message = errorData?.error || errorData?.details || errorData?.message || error.message;
      
      // Log detailed error information for debugging
      console.error('API Error Details:', {
        status,
        errorData,
        message,
        fullResponse: error.response,
        requestData: error.config?.data
      });
      
      switch (status) {
        case 400:
          // Handle specific validation errors for leverage settings
          if (message.includes('globalMaxLeverage')) {
            return new Error('Invalid leverage settings: globalMaxLeverage is fixed at 500 and cannot be modified');
          } else if (message.includes('Missing required fields')) {
            // Log more details about missing fields
            console.error('Missing required fields error. Request data:', error.config?.data);
            console.error('Error response details:', errorData);
            return new Error(`Validation Error: Missing required fields. Please check all required fields are filled.`);
          } else if (message.includes('leverage')) {
            return new Error(`Invalid leverage settings: ${message}`);
          }
          return new Error(`Validation Error: ${message}`);
        case 401:
          return new Error('Unauthorized: Please check your authentication');
        case 403:
          // Handle specific 403 errors based on the API specification
          if (message.includes('globalMaxLeverage')) {
            return new Error('Forbidden: globalMaxLeverage is fixed at 500 and cannot be changed');
          } else if (message.includes('leverage')) {
            return new Error(`Invalid leverage settings: ${message}`);
          } else if (message.includes('default group')) {
            return new Error('Forbidden: Cannot edit default group properties');
          }
          return new Error('Forbidden: You don\'t have permission to perform this action');
        case 404:
          return new Error('Not Found: The requested resource was not found');
        case 500:
          return new Error('Server Error: Please try again later');
        default:
          return new Error(message || `Error ${status}: ${error.message}`);
      }
    } else if (error.request) {
      // Network error
      return new Error('Network Error: Unable to connect to the server');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // Check if a group is the default group
  isDefaultGroup(group) {
    return group && group.isDefault === true;
  }

  // Check if a group can be edited (non-default groups can be fully edited)
  canEditGroup(group) {
    return !this.isDefaultGroup(group);
  }

  // Get editable fields for a group (default groups have restrictions)
  getEditableFields(group) {
    if (this.isDefaultGroup(group)) {
      // Default groups can only edit certain fields according to API spec
      return ['leverageSettings', 'commission', 'swap', 'defaultSpread', 'maxPositionSize', 'allowedInstruments'];
    }
    // Non-default groups can edit all fields
    return null; // null means all fields are editable
  }

  // Transform group data to include required global leverage fields
  transformGroupData(groupData) {
    const transformed = { ...groupData };
    
    // Ensure leverageSettings contains both category-specific and global settings
    if (transformed.leverageSettings) {
      const allowedCategories = ['stocks', 'crypto', 'indices', 'commodities', 'forex'];
      const filteredLeverageSettings = {};
      
      Object.entries(transformed.leverageSettings).forEach(([key, value]) => {
        if (allowedCategories.includes(key) && typeof value === 'object') {
          // Keep category-specific settings
          filteredLeverageSettings[key] = {
            maxLeverage: value.maxLeverage,
            currentLeverage: value.currentLeverage
          };
        } else if (key === 'globalMaxLeverage' || key === 'globalCurrentLeverage') {
          // Keep global leverage settings as they are required by the server
          filteredLeverageSettings[key] = value;
        }
      });
      
      // If global leverage settings are missing, add them with fixed values as required by backend
      if (!filteredLeverageSettings.globalMaxLeverage) {
        filteredLeverageSettings.globalMaxLeverage = 500;
      }
      if (!filteredLeverageSettings.globalCurrentLeverage) {
        filteredLeverageSettings.globalCurrentLeverage = 500;
      }
      
      // Ensure global leverage values are exactly 500 as required by backend
      filteredLeverageSettings.globalMaxLeverage = 500;
      filteredLeverageSettings.globalCurrentLeverage = 500;
      
      transformed.leverageSettings = filteredLeverageSettings;
    }
    
    return transformed;
  }
}

export const groupsAPI = new GroupsAPI();
export default groupsAPI;
